// core/cobroke-rules.mjs — the co-broke split's rules (0449), PURE.
//
// ⚠️ THIS FILE HAS A TWIN: `server-new-libs/lib/cobroke_rules.mjs`. Same content, different
// filename (hyphen here, underscore there — the same arrangement as binder-rules /
// binder_rules and match-score / match_score, and the same warning applies: check every
// difference before syncing the pair, because some differences are load-bearing).
//
// It exists as its own module for the reason binder-rules.mjs states: this decides about
// MONEY, and money rules get a test file. Inside the route closure it would be unimportable
// and therefore untestable.
//
// ── WHAT A SPLIT IS ─────────────────────────────────────────────────────────────
// The middle stage of a three-stage chain (0449's header has the whole thing):
//
//   cooperation      100% × 1000 USD/month   →  1000   the fee the OWNER agreed
//   co-broke split   50 / 50 with Giorgi     →   500   OUR share          ← here
//   agency ↔ agent   agentDealIncome(500)    →   ___   the agent's income
//
// So this file answers exactly one question — what fraction of the owner's fee is ours —
// and deliberately does not know what the fee IS. The amount is a projection computed at
// the point of display from whatever the fee turns out to be, which is why the ratio
// survives every later correction to it.
//
// ⚠️ IT HOLDS NO DEFAULT PERCENT. 50 is agency POLICY and lives in the scheduler `config`
// table beside desk_ranking_points and fx_to_gel, where the owner can change it. A constant
// here would be a second definition of the same number, and the two would disagree the first
// time it moved.

const round2 = n => Math.round(n * 100) / 100

// ⚠️ NOT `Number(x)`. Number(null) and Number('') are both 0, which sails through
// Number.isFinite — so an UNKNOWN fee was reported as a fee of ZERO, and "we earn nothing
// on this deal" is a very different statement from "nobody has said yet". Caught by the two
// unknown-fee cases in the test file, which is why they are there.
const num = x => (x === null || x === undefined || x === '' ? null : Number(x))

// `num` guards the EMPTIES; this also guards the garbage. `num('half')` is NaN, which is not
// null and sails through every `!== null` check — and NaN survives JSON.stringify as `null`,
// so a percent nobody can read would land in the database as "no commission" rather than as
// an error. Used wherever a stored RATIO is read, which is the input nobody validates twice.
const finite = x => { const n = num(x); return n !== null && Number.isFinite(n) ? n : null }

/**
 * Validate a co-broke split.
 *
 * @param input  undefined  → not mentioned; leave whatever is stored alone
 *               null | []  → clear it (this deal has no co-broke)
 *               array      → [{ side:'us'|'external', party_id?, percent }]
 * @returns {error} | {value}   value === undefined means "not mentioned"
 *                              value === []        means "clear"
 */
export function validateSplit(input) {
  if (input === undefined) return { value: undefined }
  if (input === null) return { value: [] }
  if (!Array.isArray(input)) return { error: 'commission_split must be an array or null' }
  if (!input.length) return { value: [] }

  const rows = []
  let ours = 0
  const seenParties = new Set()
  for (const s of input) {
    const side = String(s?.side || '')
    if (side !== 'us' && side !== 'external') return { error: "each split row needs side 'us' or 'external'" }
    const pct = Number(s?.percent)
    if (!Number.isFinite(pct) || pct <= 0 || pct > 100) return { error: 'each split percent must be 0–100' }
    if (side === 'us') {
      ours += 1
      if (ours > 1) return { error: "the split has two 'us' rows — our half is one number" }
      if (s.party_id) return { error: "the 'us' row names no party: it is the agency, not an agent" }
      rows.push({ side: 'us', party_id: null, percent: pct })
      continue
    }
    const pid = String(s?.party_id || '')
    if (!pid) return { error: 'an external split row must name the party it pays' }
    if (seenParties.has(pid)) return { error: 'the same external party is listed twice' }
    seenParties.add(pid)
    rows.push({ side: 'external', party_id: pid, percent: pct })
  }

  // ⚠️ BOTH HALVES ARE REQUIRED, and neither rule is pedantry.
  //
  // No 'us' row: the record would say what we owe the other agency and never what we keep,
  // so the close path — which reads our percent to decide what reaches payroll — would have
  // to guess it as "the remainder", i.e. re-derive a money figure the agent never stated.
  //
  // No external row: a lone 'us' at 100% is not a split, it is the ABSENCE of one, and the
  // two must stay distinguishable. Storing it would put a co-broke record on every ordinary
  // solo deal and make "how often do we split" unanswerable. Clearing is how you say no.
  if (!ours) return { error: "a split needs our own row — say what WE keep, do not leave it to be inferred" }
  if (!seenParties.size) return { error: 'a split with nobody to split with is not a split — clear it instead' }

  const total = rows.reduce((a, r) => a + r.percent, 0)
  // Two decimals of tolerance, same as the binder's: thirds are real (33.33 × 3) and
  // rejecting them would force agents to lie by a hundredth.
  if (Math.abs(total - 100) > 0.05) return { error: `the split totals ${round2(total)}%, must be 100%` }
  return { value: rows }
}

/** Our percent of the owner's fee, or null when no split is recorded (i.e. all of it). */
export function ourSharePercent(rows) {
  const us = (rows || []).find(r => r.side === 'us')
  // ⚠️ `num`, not bare Number (2026-08-15). `Number(null)` is 0 and `Number('half')` is NaN;
  // the first would silently say "we keep nothing" and the second would propagate a NaN into
  // a money field, where JSON.stringify writes it as null. This is the same coercion the
  // header of this file already forbids for the FEE — the ratio was left undefended.
  return us ? finite(us.percent) : null
}

/**
 * OUR half of a fee, in money. The projection — never stored, recomputed wherever shown, so
 * it can never silently disagree with the ratio it came from (coopMath.js states the same
 * rule for every other commission figure in this system).
 *
 * No split recorded → the whole fee is ours, which is the truth for the ~90% of deals with
 * no counterparty. Returns null only when the fee itself is unknown, so a caller can tell
 * "nothing to show" from "zero".
 */
export function ourShareOf(gross, rows) {
  const g = num(gross)
  if (g === null || !Number.isFinite(g)) return null
  const list = rows || []
  // ⚠️ NO SPLIT AT ALL and A SPLIT THAT DOES NOT NAME OUR SHARE ARE DIFFERENT FACTS, and the
  // difference is payroll (fixed 2026-08-15). Both used to answer "the whole fee is ours":
  // `ourShareOf(1000, [{side:'external',percent:50}])` returned 1000. `validateSplit` refuses
  // to STORE that shape, but this is a pure helper documented as safe, its caller gates on
  // `rows.length` alone, and its result goes to `commission_amount` → agentDealIncome → the
  // leaderboard. A legacy, hand-written or half-migrated row set without an `us` row would
  // have paid our agents out of the other agency's half — the exact failure DealCloseModal's
  // header says this code exists to prevent.
  // An empty list is the honest "no counterparty" case (~90% of deals) and keeps the fee.
  if (!list.length) return round2(g)
  const pct = ourSharePercent(list)
  if (pct === null) return null      // a split exists but will not say what we keep → unknown
  return round2(g * pct / 100)
}

/**
 * What each side is owed, for display. Same projection, one row per side, so the panel and
 * the close screen cannot print different arithmetic from the same ratio.
 */
export function splitAmounts(gross, rows) {
  const g = num(gross)
  const known = g !== null && Number.isFinite(g)
  return (rows || []).map(r => {
    // `num`, not Number — see ourSharePercent. A row whose percent is null/blank/garbage
    // must render as "unknown", never as a confident 0 money row beside real ones.
    const pct = finite(r.percent)
    return { ...r, amount: (known && pct !== null) ? round2(g * pct / 100) : null }
  })
}
